import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Calendar, Users, MapPin, Clock, ChevronRight, AlertCircle, CheckCircle, Plus, Minus, Package, Loader2 } from 'lucide-react';
import CalendarService from '../../services/CalendarService';
import { CalendarSlot } from '../../types/calendar.types';
import CalendarView from '../calendar/CalendarView';
import TimeSlotPicker from '../calendar/TimeSlotPicker';
import AvailabilityIndicator from '../calendar/AvailabilityIndicator';
import { CustomerSelector } from './CustomerSelector';
import { Customer } from '../common/CustomerDialog';
import AddonSelector from './AddonSelector';
import adminService from '../../services/adminService';
import type { AddOnCategory, AddOnService } from '../../services/adminService';
import { calculateTieredPrice, TourTypeWithPriceTiers } from '../../utils/pricingUtils';
import VoucherCodeInput, { ValidatedVoucher } from './VoucherCodeInput';
import '../../styles/components/BookingFlow.css';

import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../config/api.config';

// Tour type from API
interface TourTypeOption {
  id: number;
  code: string;
  name: string;
  isActive: boolean;
}

// Types for new auto-calculated addon system
interface CalculatedAddonDto {
  addOnServiceId: number;
  addOnServiceName: string;
  pricingType: string;
  quantityTotal: number;
  quantityFree: number;
  quantityPaid: number;
  pricePerUnit: number;
  grossPricePerUnit?: number; // Bruttopreis pro Einheit (from Backend)
  taxRate?: number; // Steuersatz (z.B. 0.19 für 19%, 0.07 für 7%)
  subtotal: number;
  isManuallyAdjusted: boolean;
  isOptionalChoice?: boolean; // If true, this is an optional choice addon (e.g., meal selection)
  isIncludedInTourPrice?: boolean; // If true, addon is included in tour price (no extra charge)
  quantityMultiplier?: number; // Multiplier for optional inclusive addons (default: 1)
}

interface SelectedAddOn {
  serviceId: number;
  service: AddOnService;
  quantity: number;
}

interface BookingData {
  date: Date | null;
  slot: CalendarSlot | null;
  tourType: string;
  participants: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company?: string;
  notes?: string;
}

const BookingFlow: React.FC = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetStatus = searchParams.get('presetStatus');
  // Helper function for German currency formatting
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  // Helper function to map frontend variant to backend variant
  const mapVariantToBackend = (variant: string): string => {
    const mapping: { [key: string]: string } = {
      'STANDARD': 'Standard',
      'PREMIUM': 'Premium',
      'ALL_IN_PREMIUM': 'AllInPremium',
      'ALL_IN': 'AllInPremium',
      'EXKLUSIV': 'Exclusive',
      'EXCLUSIVE': 'Exclusive'
    };
    return mapping[variant] || 'Standard';
  };

  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  // Map presetStatus from URL to booking status
  const getInitialStatus = () => {
    if (presetStatus === 'Angebot') return 'OfferSent';
    if (presetStatus === 'Option') return 'Option';
    return 'Confirmed';
  };
  const [bookingStatus, setBookingStatus] = useState<string>(getInitialStatus());
  const [createdBooking, setCreatedBooking] = useState<any>(null); // BUG FIX: Store API response for confirmation
  // T-042 (11.09.2026): Empfaenger der erfolgreich versendeten Angebots-Mail (null = noch nicht versendet).
  // Der Status "Angebot" (OfferSent) entsteht beim Speichern, die Mail geht erst per Knopf raus.
  const [quoteEmailSentTo, setQuoteEmailSentTo] = useState<string | null>(null);
  const [isSendingOfferEmail, setIsSendingOfferEmail] = useState(false); // T-052
  const [isSubmitting, setIsSubmitting] = useState(false); // Prevent double submission
  const [optionValidUntil, setOptionValidUntil] = useState<string>(''); // Stichtag für Options-Buchungen
  // T-081 Altfall-Übernahme (v1.8.17): Buchung wurde vor dem Systemstart im Altverfahren gebucht,
  // Anzahlung dort ggf. schon fakturiert und bezahlt (kein neuer Beleg, keine Mail, Schlussrechnung zieht ab)
  const [isLegacyImport, setIsLegacyImport] = useState<boolean>(false);
  const [legacyDepositAmount, setLegacyDepositAmount] = useState<string>('');
  const [legacyDepositPaidAt, setLegacyDepositPaidAt] = useState<string>('');
  const [legacyInvoiceNumber, setLegacyInvoiceNumber] = useState<string>('');
  const [legacyNote, setLegacyNote] = useState<string>('');
  const [calendarKey, setCalendarKey] = useState<number>(0);
  const [appliedVoucher, setAppliedVoucher] = useState<ValidatedVoucher | null>(null); // Gutschein für Admin-Buchung
  const [bookingData, setBookingData] = useState<BookingData>({
    date: null,
    slot: null,
    tourType: 'ALLE_TYPEN',
    participants: 1,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  // Teilnehmer-Kategorien (Anlage v1.3 Abschnitt 2.1, Decisions-Log 13.07.2026):
  // Erwachsene ab 13 (Vollpreis, einzige Staffelzaehler), Kinder 6-12 (60%),
  // Kinder 3-5 (20%), Kinder 0-2 (frei, ohne Sitzplatz), Freiplaetze (frei, unbegrenzt)
  const [categories, setCategories] = useState({
    adults: 1,
    children6to12: 0,
    children3to5: 0,
    children0to2: 0,
    freeSeats: 0,
  });

  const [availableSlots, setAvailableSlots] = useState<CalendarSlot[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [showTimeSlots, setShowTimeSlots] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);

  // NEW: Tour types loaded from API
  const [tourTypes, setTourTypes] = useState<TourTypeOption[]>([]);
  const [loadingTourTypes, setLoadingTourTypes] = useState(false);

  // Tour types with price tiers for tiered pricing calculation
  const [tourTypesWithPricing, setTourTypesWithPricing] = useState<TourTypeWithPriceTiers[]>([]);

  // NEW: Auto-calculated addon state
  const [calculatedAddons, setCalculatedAddons] = useState<CalculatedAddonDto[]>([]);

  // Track the parameters for which addons were calculated
  // This is used to detect when we need to recalculate (e.g., participant count changed)
  const [lastAddonParams, setLastAddonParams] = useState<{ participants: number; tourType: string } | null>(null);

  // Reset addons when participant count or tour type changes
  // This ensures addons are recalculated with the new parameters
  useEffect(() => {
    if (lastAddonParams && calculatedAddons.length > 0) {
      const currentTourType = bookingData.slot?.tourType || bookingData.tourType;
      const paramsChanged =
        lastAddonParams.participants !== bookingData.participants ||
        lastAddonParams.tourType !== currentTourType;

      if (paramsChanged) {
        console.log('[BookingFlow] Addon params changed, resetting addons', {
          old: lastAddonParams,
          new: { participants: bookingData.participants, tourType: currentTourType }
        });
        setCalculatedAddons([]);
        setLastAddonParams(null);
      }
    }
  }, [bookingData.participants, bookingData.tourType, bookingData.slot?.tourType, lastAddonParams, calculatedAddons.length]);

  // OLD: Keep old addon system for backwards compatibility (can be removed later)
  const [addOnCategories, setAddOnCategories] = useState<AddOnCategory[]>([]);
  const [selectedAddOns, setSelectedAddOns] = useState<SelectedAddOn[]>([]);
  const [loadingAddOns, setLoadingAddOns] = useState(false);
  const [showAddOnModal, setShowAddOnModal] = useState(false);
  const [addOnFormData, setAddOnFormData] = useState({
    categoryId: 1,
    name: '',
    description: '',
    grossPrice: 0.01, // BRUTTO-FIRST
    pricingType: 'PerPerson' as 'PerPerson' | 'PerGroup' | 'Fixed' | 'PerDay' | 'PerNight',
    taxRate: 0.19,
    isActive: true,
    sortOrder: 0
  });

  // Add responsive listener
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const newIsDesktop = width >= 992;
      setIsDesktop(newIsDesktop);
    };

    // Initial check
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load tour types from API on mount
  useEffect(() => {
    const loadTourTypes = async () => {
      setLoadingTourTypes(true);
      try {
        const response = await fetch(`${API_BASE_URL}/tour-types`);
        if (response.ok) {
          const data = await response.json();
          setTourTypes(data.filter((t: TourTypeOption) => t.isActive));
          // Also save tour types with price tiers for tiered pricing calculation
          setTourTypesWithPricing(data);
          console.log('[BookingFlow] Loaded tour types with price tiers:', data);
        } else {
          console.error('[BookingFlow] Failed to load tour types:', response.status);
        }
      } catch (error) {
        console.error('[BookingFlow] Error loading tour types:', error);
      } finally {
        setLoadingTourTypes(false);
      }
    };

    loadTourTypes();
  }, []);

  // Load AddOns when reaching step 3
  useEffect(() => {
    if (step === 3) {
      loadAddOns();
    }
  }, [step]);

  // Load available slots when date/type/participants change
  useEffect(() => {
    if (selectedDate) {
      loadAvailableSlots();
    }
  }, [selectedDate, bookingData.tourType, bookingData.participants]);

  const loadAddOns = async () => {
    setLoadingAddOns(true);
    try {
      const categories = await adminService.getAddOnCategories(false); // only active
      setAddOnCategories(categories);
    } catch (error) {
      console.error('Error loading AddOns:', error);
    } finally {
      setLoadingAddOns(false);
    }
  };

  const handleSaveNewAddOn = async () => {
    try {
      const newService = await adminService.createAddOnService({
        ...addOnFormData,
        sortOrder: addOnFormData.sortOrder || 0
      });

      // Reload AddOns to get the new service
      await loadAddOns();

      // Auto-select the newly created service with quantity 1
      const newAddOnService: AddOnService = {
        id: newService.id,
        categoryId: newService.categoryId,
        name: newService.name,
        description: newService.description,
        price: newService.price,
        grossPrice: newService.grossPrice, // BRUTTO-FIRST
        pricingType: newService.pricingType,
        taxRate: newService.taxRate,
        isActive: newService.isActive,
        sortOrder: newService.sortOrder,
        requiresManualConfirmation: newService.requiresManualConfirmation || false
      };

      setSelectedAddOns(prev => [...prev, {
        serviceId: newService.id,
        service: newAddOnService,
        quantity: 1
      }]);

      // Reset form and close modal
      setAddOnFormData({
        categoryId: 1,
        name: '',
        description: '',
        grossPrice: 0.01, // BRUTTO-FIRST
        pricingType: 'PerPerson',
        taxRate: 0.19,
        isActive: true,
        sortOrder: 0
      });
      setShowAddOnModal(false);

      alert('Zusatzoption erfolgreich erstellt und zur Buchung hinzugefügt!');
    } catch (error: any) {
      console.error('Error creating AddOn:', error);
      alert(`Fehler beim Erstellen: ${error.message || 'Unbekannter Fehler'}`);
    }
  };

  const loadAvailableSlots = async () => {
    if (!selectedDate) return;

    setLoadingSlots(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const isWeekend = selectedDate.getDay() === 0 || selectedDate.getDay() === 6;
      const tourTypeToUse = isWeekend ? undefined : bookingData.tourType;

      console.log('[BookingFlow] loadAvailableSlots called');
      console.log('[BookingFlow] selectedDate:', dateStr, 'day:', selectedDate.getDay());
      console.log('[BookingFlow] isWeekend:', isWeekend);
      console.log('[BookingFlow] tourTypeToUse (passed to API):', tourTypeToUse);

      const slots = await CalendarService.getAvailableSlots(
        selectedDate,
        selectedDate,
        tourTypeToUse
      );

      console.log('[BookingFlow] Loaded calendar slots from API:', slots);
      setAvailableSlots(slots);
      setShowTimeSlots(true);
    } catch (error) {
      console.error('[BookingFlow] Error loading slots:', error);
      setAvailableSlots([]);
      setShowTimeSlots(true);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDateSelection = (date: Date) => {
    setSelectedDate(date);
    setBookingData(prev => ({ ...prev, date }));
    setShowTimeSlots(true);
  };

  const handleSlotSelection = (slot: CalendarSlot) => {
    // BUG #32 FIX: FixedPrice tour types (PRIVAT, HOCHZEIT, EXKLUSIV) should KEEP the admin-selected type
    // For regular tour types, use the slot's tour type
    const fixedPriceTourTypes = ['PRIVAT', 'HOCHZEIT', 'EXKLUSIV', 'EXCLUSIVE'];
    const currentTourTypeNormalized = bookingData.tourType.toUpperCase().replace(/[_\s-]+/g, '_');
    const isFixedPriceTourType = fixedPriceTourTypes.some(fp =>
      currentTourTypeNormalized.includes(fp) || currentTourTypeNormalized.startsWith(fp)
    );

    // For FixedPrice tour types (PRIVAT, HOCHZEIT), keep the admin-selected tour type
    // For regular tour types, use the slot's tour type
    const tourTypeToUse = isFixedPriceTourType
      ? bookingData.tourType  // Keep admin-selected FixedPrice tour type
      : slot.tourType.toUpperCase().replace(/[_\s-]+/g, '_');  // Use slot's tour type

    console.log('[BookingFlow] Slot selected, slot tour type:', slot.tourType);
    console.log('[BookingFlow] Admin-selected tour type:', bookingData.tourType, 'isFixedPrice:', isFixedPriceTourType);
    console.log('[BookingFlow] Using tour type for booking:', tourTypeToUse);

    setBookingData(prev => ({
      ...prev,
      slot,
      date: selectedDate,
      tourType: tourTypeToUse  // Use appropriate tour type based on pricing mode
    }));
    setStep(2);
  };

  const handleTourTypeChange = (tourType: string) => {
    setBookingData(prev => ({ ...prev, tourType }));
    setBookingData(prev => ({ ...prev, slot: null }));
  };

  const handleParticipantsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const participants = parseInt(e.target.value) || 0;
    setBookingData(prev => ({ ...prev, participants, slot: null }));
    // Gesamtzahl direkt geaendert: Differenz auf Erwachsene anwenden
    setCategories(prev => {
      const others = prev.children6to12 + prev.children3to5 + prev.children0to2 + prev.freeSeats;
      return { ...prev, adults: Math.max(0, participants - others) };
    });
  };

  // Kategorie geaendert: Gesamtzahl neu berechnen
  const handleCategoryChange = (key: 'adults' | 'children6to12' | 'children3to5' | 'children0to2' | 'freeSeats', value: number) => {
    const val = Math.max(0, value);
    setCategories(prev => {
      const next = { ...prev, [key]: val };
      const total = next.adults + next.children6to12 + next.children3to5 + next.children0to2 + next.freeSeats;
      setBookingData(bd => ({ ...bd, participants: total }));
      return next;
    });
  };

  // Kategorie-Eingabefelder (in beiden Layout-Varianten verwendet)
  const renderCategoryInputs = () => (
    <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#eef4fb', borderRadius: '8px' }}>
      <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
        Teilnehmer-Kategorien (Staffelpreis richtet sich NUR nach Erwachsenen):
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.5rem' }}>
        {([
          { key: 'adults' as const, label: 'Erwachsene (ab 13)' },
          { key: 'children6to12' as const, label: 'Kinder 6-12 (60%)' },
          { key: 'children3to5' as const, label: 'Kinder 3-5 (20%)' },
          { key: 'children0to2' as const, label: 'Kinder 0-2 (frei, ohne Sitzplatz)' },
          { key: 'freeSeats' as const, label: 'Freiplätze (frei)' },
        ]).map(({ key, label }) => (
          <div key={key}>
            <label style={{ fontSize: '0.78rem', display: 'block' }}>{label}</label>
            <input
              type="number"
              min="0"
              max="200"
              value={categories[key]}
              onChange={(e) => handleCategoryChange(key, parseInt(e.target.value) || 0)}
              style={{ width: '100%' }}
            />
          </div>
        ))}
      </div>
    </div>
  );

  const mapTourTypeToVariant = (tourType: string): string => {
    const normalized = tourType.toUpperCase().replace(/[_\s-]+/g, '_');

    // Check for ALL IN PREMIUM variations
    if (normalized.includes('ALL') && normalized.includes('IN')) {
      return 'AllInPremium';
    }

    // Check for PREMIUM (but not ALL IN PREMIUM)
    if (normalized.includes('PREMIUM')) {
      return 'Premium';
    }

    // Check for EXCLUSIVE/EXKLUSIV
    if (normalized.includes('EXKLUSIV') || normalized.includes('EXCLUSIVE')) {
      return 'Exclusive';
    }

    // Default to Standard
    return 'Standard';
  };

  const handleAddOnQuantityChange = (service: AddOnService, delta: number) => {
    setSelectedAddOns(prev => {
      const existing = prev.find(a => a.serviceId === service.id);
      if (!existing) {
        if (delta > 0) {
          return [...prev, { serviceId: service.id, service, quantity: 1 }];
        }
        return prev;
      }

      const newQuantity = existing.quantity + delta;
      if (newQuantity <= 0) {
        return prev.filter(a => a.serviceId !== service.id);
      }

      return prev.map(a =>
        a.serviceId === service.id
          ? { ...a, quantity: newQuantity }
          : a
      );
    });
  };

  const calculateAddOnTotal = () => {
    // NEW: Use calculated addons with BRUTTO prices
    if (calculatedAddons.length > 0) {
      // FIX: Use BRUTTO prices (addon.subtotal is NETTO)
      return calculatedAddons.reduce((total, addon) => {
        const grossPrice = addon.grossPricePerUnit ?? addon.pricePerUnit * (1 + (addon.taxRate ?? 0.19));
        return total + addon.quantityPaid * grossPrice;
      }, 0);
    }

    // OLD: Fallback to old addon system (assuming prices are already BRUTTO)
    return selectedAddOns.reduce((total, addon) => {
      const price = addon.service.pricingType === 'PerPerson'
        ? addon.service.price * bookingData.participants * addon.quantity
        : addon.service.price * addon.quantity;
      return total + price;
    }, 0);
  };

  // NEU: Steueraufschlüsselung für Extras berechnen (Basis = BRUTTO)
  const calculateAddonTaxBreakdown = (): {
    totalGross: number;
    totalNet: number;
    vat7: number;
    vat19: number
  } => {
    let totalGross = 0;
    let totalNet = 0;
    let vat7 = 0;
    let vat19 = 0;

    calculatedAddons.forEach(addon => {
      if (addon.quantityPaid === 0) return;

      const grossPrice = addon.grossPricePerUnit ?? addon.pricePerUnit * (1 + (addon.taxRate ?? 0.19));
      const grossSubtotal = addon.quantityPaid * grossPrice;
      const taxRate = addon.taxRate ?? 0.19;

      // Netto aus Brutto berechnen
      const netSubtotal = grossSubtotal / (1 + taxRate);
      const vatAmount = grossSubtotal - netSubtotal;

      totalGross += grossSubtotal;
      totalNet += netSubtotal;

      // MwSt. nach Steuersatz aufschlüsseln
      if (Math.abs(taxRate - 0.07) < 0.001) {
        vat7 += vatAmount;
      } else {
        vat19 += vatAmount;
      }
    });

    return { totalGross, totalNet, vat7, vat19 };
  };

  // Validate optional choice addons - sum must equal participant count × multiplier
  // T-033 (02.09.2026, Kundin-Befund "Inklusivleistung bei Kleinkindern wird erzwungen"): Die Pflichtauswahl
  // pro Teilnehmer gilt nur fuer leistungsberechtigte Gaeste. Kinder 0-2 (kostenfrei, ohne Leistung, Anlage
  // v1.3 Abschnitt 2.1 Punkt 9) zaehlen NICHT mit, genau wie beim AddonSelector (participantCount) und in
  // BookingEditDialog (mealRelevantCount). Vorher verlangte die Pruefung 47 Portionen bei 44 Berechtigten.
  const mealRelevantCount = Math.max(0, bookingData.participants - (categories.children0to2 || 0));

  const validateOptionalChoiceAddons = (): { isValid: boolean; total: number; expected: number; multiplier: number; hasOptionalChoice: boolean } => {
    const optionalChoiceAddons = calculatedAddons.filter(a => a.isOptionalChoice);

    if (optionalChoiceAddons.length === 0) {
      return { isValid: true, total: 0, expected: mealRelevantCount, multiplier: 1, hasOptionalChoice: false };
    }

    // Get the multiplier from the first optional choice addon
    // (all optional choice addons for a tour type should have the same multiplier)
    const multiplier = optionalChoiceAddons[0]?.quantityMultiplier || 1;
    const expected = mealRelevantCount * multiplier;

    const total = optionalChoiceAddons.reduce((sum, addon) => sum + addon.quantityTotal, 0);
    const isValid = total === expected;

    return { isValid, total, expected, multiplier, hasOptionalChoice: true };
  };

  // Check if we can proceed to step 4
  const canProceedToStep4 = (): boolean => {
    const validation = validateOptionalChoiceAddons();
    // Can proceed if there are no optional choice addons OR if validation passes
    return !validation.hasOptionalChoice || validation.isValid;
  };

  const handleSubmit = async () => {
    // Prevent double submission
    if (isSubmitting) {
      console.log('Submission already in progress, ignoring...');
      return;
    }

    try {
      setIsSubmitting(true);
      console.log('Submitting booking:', bookingData);
      console.log('Calculated AddOns:', calculatedAddons);

      if (!bookingData.slot || !bookingData.date) {
        alert('Bitte wählen Sie einen Termin und eine Uhrzeit aus.');
        return;
      }

      if (!selectedCustomer) {
        alert('Bitte wählen Sie einen Kunden aus.');
        return;
      }

      // Validierung für Option-Status: Stichtag erforderlich
      if (bookingStatus === 'Option' && !optionValidUntil) {
        alert('Bitte geben Sie einen Stichtag für die Option an.');
        return;
      }

      // Validierung für Angebot-Status: Gültigkeitsdatum erforderlich
      if (bookingStatus === 'OfferSent' && !optionValidUntil) {
        alert('Bitte geben Sie ein Gültigkeitsdatum für das Angebot an.');
        return;
      }

      // T-081 Altfall-Übernahme: Betrag plausibel (0 bis Gesamtpreis, Prüfung unten nach Preisberechnung),
      // Zahldatum nicht in der Zukunft
      const legacyAmountParsed = isLegacyImport && legacyDepositAmount.trim() !== ''
        ? parseFloat(legacyDepositAmount.replace(',', '.'))
        : 0;
      if (isLegacyImport && (isNaN(legacyAmountParsed) || legacyAmountParsed < 0)) {
        alert('Bitte geben Sie die bereits gezahlte Anzahlung als Betrag ein (z. B. 330,00), oder lassen Sie das Feld leer.');
        return;
      }
      if (isLegacyImport && legacyDepositPaidAt && legacyDepositPaidAt > new Date().toISOString().split('T')[0]) {
        alert('Das Zahldatum der Anzahlung darf nicht in der Zukunft liegen.');
        return;
      }
      if (isLegacyImport && legacyAmountParsed > 0 && !legacyDepositPaidAt) {
        alert('Bitte geben Sie an, wann die Anzahlung gezahlt wurde (Datum).');
        return;
      }

      const variant = mapTourTypeToVariant(bookingData.slot.tourType);

      // NEW: Map calculated addons to API format
      // Filter out addons with zero quantity and map to backend format
      // FIX: Send BRUTTO prices to backend (addon.pricePerUnit is NETTO, addon.grossPricePerUnit is BRUTTO)
      const addOns = calculatedAddons
        .filter(addon => addon.quantityTotal > 0)
        .map(addon => {
          const grossPrice = addon.grossPricePerUnit ?? addon.pricePerUnit * (1 + (addon.taxRate ?? 0.19));
          const grossSubtotal = addon.quantityPaid * grossPrice;
          return {
            addOnServiceId: addon.addOnServiceId,
            quantity: addon.quantityTotal,
            quantityFree: addon.quantityFree,
            quantityPaid: addon.quantityPaid,
            pricePerUnit: grossPrice, // BRUTTO Einzelpreis
            subtotal: grossSubtotal, // BRUTTO Zwischensumme
            isManuallyAdjusted: addon.isManuallyAdjusted
          };
        });

      // Calculate tiered price: Staffel richtet sich AUSSCHLIESSLICH nach Vollzahlern (Erwachsene),
      // Decisions-Log 13.07.2026 / Anlage v1.3 Abschnitt 2.1 Punkt 4
      const tourTypeCodeForSubmit = bookingData.slot?.tourType || bookingData.tourType;
      const fallbackPriceForSubmit = bookingData.slot?.pricePerPerson || bookingData.slot?.basePrice || 0;
      const tieredPriceForSubmit = calculateTieredPrice(
        tourTypeCodeForSubmit,
        categories.adults,
        tourTypesWithPricing,
        fallbackPriceForSubmit
      );
      const pricePerPersonForSubmit = tieredPriceForSubmit.pricePerPerson;
      // IMPORTANT: For FixedPrice tours, tourPrice IS the fixed price (not multiplied by participants)
      const isFixedPriceSubmit = tieredPriceForSubmit.pricingMode === 'FixedPrice';
      // Kategorie-Preise: Erwachsene 100%, Kinder 6-12 zu 60%, Kinder 3-5 zu 20%, Kinder 0-2 und Freiplaetze 0
      const round2 = (x: number) => Math.round(x * 100) / 100;
      const categoryTourPrice =
        categories.adults * pricePerPersonForSubmit +
        categories.children6to12 * round2(pricePerPersonForSubmit * 0.6) +
        categories.children3to5 * round2(pricePerPersonForSubmit * 0.2);
      const tourPrice = isFixedPriceSubmit ? pricePerPersonForSubmit : categoryTourPrice;

      console.log('[handleSubmit] Tiered price calculation:', {
        tourTypeCode: tourTypeCodeForSubmit,
        participants: bookingData.participants,
        categories,
        pricePerPerson: pricePerPersonForSubmit,
        tierDescription: tieredPriceForSubmit.tierDescription,
        pricingMode: tieredPriceForSubmit.pricingMode,
        isFixedPrice: isFixedPriceSubmit,
        tourPrice
      });

      // FIX: Use BRUTTO prices for add-on total calculation
      const addOnTotalForPayload = calculatedAddons
        .filter(a => a.quantityTotal > 0)
        .reduce((sum, addon) => {
          const grossPrice = addon.grossPricePerUnit ?? addon.pricePerUnit * (1 + (addon.taxRate ?? 0.19));
          return sum + addon.quantityPaid * grossPrice;
        }, 0);
      const calculatedTotalPrice = tourPrice + addOnTotalForPayload;

      // Apply voucher discount if present
      const voucherDiscount = appliedVoucher?.amountToApply || 0;
      const finalTotalPrice = Math.max(0, calculatedTotalPrice - voucherDiscount);

      // T-081: Alt-Anzahlung darf den Gesamtpreis nicht übersteigen
      if (isLegacyImport && legacyAmountParsed > finalTotalPrice + 0.005) {
        alert(`Die bereits gezahlte Anzahlung (${formatCurrency(legacyAmountParsed)}) ist höher als der Gesamtpreis der Buchung (${formatCurrency(finalTotalPrice)}). Bitte Betrag prüfen.`);
        return;
      }

      const bookingPayload = {
        floatTripId: 0,
        calendarSlotId: bookingData.slot.id, // CalendarSlot-ID für Kalender-Integration
        tourType: bookingData.slot.tourType || bookingData.tourType, // TourType für Addon-Validierung mit Multiplier
        participantCount: bookingData.participants,
        // Teilnehmer-Kategorien (Anlage v1.3 Abschnitt 2.1)
        adultCount: categories.adults,
        childCount: categories.children6to12,
        childSmallCount: categories.children3to5,
        infantCount: categories.children0to2,
        freeParticipantWithSeatCount: categories.freeSeats,
        freeParticipantNoSeatCount: 0,
        requestedDate: format(bookingData.date!, 'yyyy-MM-dd'),
        requestedTime: bookingData.slot.time,
        variant: variant,
        customerInfo: {
          customerId: selectedCustomer.id,  // BUG FIX: Send customer ID to use existing customer
          firstName: selectedCustomer.firstName,
          lastName: selectedCustomer.lastName,
          email: selectedCustomer.email,
          phone: selectedCustomer.phone,
          companyName: selectedCustomer.companyName || undefined
        },
        specialRequests: bookingData.notes || `Admin-Buchung erstellt am ${format(new Date(), 'dd.MM.yyyy HH:mm')}`,
        addOns: addOns,
        paymentPreference: 0,
        initialStatus: bookingStatus,
        bypassMinimumParticipants: true, // Admin kann alle Validierungen umgehen
        // FIX: Send calculated tiered price to backend (admin bookings use frontend price calculation)
        manualTotalPrice: finalTotalPrice,
        manualBasePrice: pricePerPersonForSubmit, // Use tiered price, not base price
        // Option-Buchung oder Angebot: Gültigkeitsdatum mitschicken
        optionValidUntil: (bookingStatus === 'Option' || bookingStatus === 'OfferSent') ? optionValidUntil : undefined,
        // Gutschein-Code für Einlösung
        voucherCode: appliedVoucher?.code || undefined,
        voucherDiscount: voucherDiscount > 0 ? voucherDiscount : undefined,
        // T-081 Altfall-Übernahme (nur Admin, Backend ignoriert die Felder für Kunden)
        isLegacyImport: isLegacyImport,
        legacyDepositAmount: isLegacyImport && legacyAmountParsed > 0 ? Math.round(legacyAmountParsed * 100) / 100 : undefined,
        legacyDepositPaidAt: isLegacyImport && legacyDepositPaidAt ? legacyDepositPaidAt : undefined,
        legacyInvoiceNumber: isLegacyImport && legacyInvoiceNumber.trim() ? legacyInvoiceNumber.trim() : undefined,
        legacyNote: isLegacyImport && legacyNote.trim() ? legacyNote.trim() : undefined
      };

      console.log('Sending booking to API:', bookingPayload);

      const response = await fetch(`${API_BASE_URL}/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(bookingPayload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Booking API error response:', errorText);

        let errorMessage = 'Buchung fehlgeschlagen';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.Message || errorData.error || errorMessage;

          if (errorData.validationErrors || errorData.ValidationErrors) {
            const validationErrors = errorData.validationErrors || errorData.ValidationErrors;
            const errorDetails = Object.entries(validationErrors)
              .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
              .join('\n');
            errorMessage = `${errorMessage}\n\nValidierungsfehler:\n${errorDetails}`;
          }
        } catch (e) {
          errorMessage = `Server-Fehler: ${response.status} ${response.statusText}\n${errorText}`;
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Booking successful:', result);

      // BUG FIX: Store created booking for confirmation display
      setCreatedBooking(result);

      // Increment calendar key to force refresh
      setCalendarKey(prev => prev + 1);

      setStep(5); // Go to confirmation step
    } catch (error: any) {
      console.error('Error submitting booking:', error);
      alert(`Fehler bei der Buchung: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderProgressBar = () => (
    <div className="booking-progress">
      <div className="progress-steps">
        <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
          <div className="step-number">1</div>
          <span>Termin</span>
        </div>
        <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
          <div className="step-number">2</div>
          <span>Kunde</span>
        </div>
        <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
          <div className="step-number">3</div>
          <span>Zusatzoptionen</span>
        </div>
        <div className={`progress-step ${step >= 4 ? 'active' : ''}`}>
          <div className="step-number">4</div>
          <span>Prüfen</span>
        </div>
        <div className={`progress-step ${step >= 5 ? 'active' : ''}`}>
          <div className="step-number">5</div>
          <span>Bestätigung</span>
        </div>
      </div>
    </div>
  );

  const renderStep1 = () => {
    if (isDesktop) {
      return (
        <div className="booking-step step-1">
          <h2>Termin und Teilnehmer auswählen</h2>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2rem',
            marginTop: '1.5rem'
          }}>
            <div style={{ width: '100%' }}>
              <h3>Wählen Sie einen Termin:</h3>
              <CalendarView
                key={calendarKey}
                onDateSelect={(date) => {
                  setSelectedDate(date);
                  setBookingData(prev => ({ ...prev, date }));
                }}
                onSelectSlot={(slot) => {
                  setBookingData(prev => ({
                    ...prev,
                    slot,
                    date: new Date(slot.date)
                  }));
                  setStep(2);
                }}
                minParticipants={bookingData.participants}
                selectedTourType={bookingData.tourType === 'ALLE_TYPEN' ? undefined : bookingData.tourType}
                isAdminBooking={true}
              />
            </div>

            <div style={{
              width: '100%',
              background: '#f8f9fa',
              padding: '1.5rem',
              borderRadius: '12px',
              border: '2px solid #e9ecef'
            }}>
              <div className="booking-options">
                <h3>Tourdetails:</h3>
                <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', marginTop: '1rem' }}>
                  <div className="option-group" style={{ flex: '1 1 300px' }}>
                    <label htmlFor="tourType">Tour-Typ:</label>
                    <select
                      id="tourType"
                      value={bookingData.tourType}
                      onChange={(e) => handleTourTypeChange(e.target.value)}
                      disabled={loadingTourTypes}
                    >
                      <option value="ALLE_TYPEN">Alle Typen</option>
                      {tourTypes.map(t => (
                        <option key={t.id} value={t.code}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="option-group" style={{ flex: '1 1 300px' }}>
                    <label htmlFor="participants">Anzahl Teilnehmer (gesamt):</label>
                    <input
                      id="participants"
                      type="number"
                      min="1"
                      max="160"
                      value={bookingData.participants}
                      onChange={handleParticipantsChange}
                    />
                  </div>
                </div>
                {renderCategoryInputs()}
              </div>

              {showTimeSlots && selectedDate && (
                <div className="time-slots-section" style={{ marginTop: '1.5rem' }}>
                  <h3>Verfügbare Termine:</h3>
                  <TimeSlotPicker
                    date={selectedDate}
                    tourType={bookingData.tourType === 'ALLE_TYPEN' ? undefined : bookingData.tourType}
                    participants={bookingData.participants}
                    onSelectSlot={handleSlotSelection}
                    isAdminBooking={true}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="booking-step step-1">
          <h2>Termin und Teilnehmer auswählen</h2>

          <div className="booking-layout">
            <div className="booking-form-section">
              <div className="booking-options">
                <h3>Tourdetails:</h3>
                <div className="option-group">
                  <label htmlFor="tourType">Tour-Typ:</label>
                  <select
                    id="tourType"
                    value={bookingData.tourType}
                    onChange={(e) => handleTourTypeChange(e.target.value)}
                    disabled={loadingTourTypes}
                  >
                    <option value="ALLE_TYPEN">Alle Typen</option>
                    {tourTypes.map(t => (
                      <option key={t.id} value={t.code}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div className="option-group">
                  <label htmlFor="participants">Anzahl Teilnehmer (gesamt):</label>
                  <input
                    id="participants"
                    type="number"
                    min="1"
                    max="160"
                    value={bookingData.participants}
                    onChange={handleParticipantsChange}
                  />
                </div>
                {renderCategoryInputs()}
              </div>

              {showTimeSlots && selectedDate && (
                <div className="time-slots-section">
                  <h3>Verfügbare Termine:</h3>
                  <TimeSlotPicker
                    date={selectedDate}
                    tourType={bookingData.tourType === 'ALLE_TYPEN' ? undefined : bookingData.tourType}
                    participants={bookingData.participants}
                    onSelectSlot={handleSlotSelection}
                    isAdminBooking={true}
                  />
                </div>
              )}
            </div>

            <div className="calendar-section" style={{ marginTop: '2rem' }}>
              <h3>Wählen Sie einen Termin:</h3>
              <CalendarView
                key={calendarKey}
                onDateSelect={(date) => {
                  setSelectedDate(date);
                  setBookingData(prev => ({ ...prev, date }));
                }}
                onSelectSlot={handleSlotSelection}
                minParticipants={bookingData.participants}
                selectedTourType={bookingData.tourType === 'ALLE_TYPEN' ? undefined : bookingData.tourType}
                isAdminBooking={true}
              />
            </div>
          </div>
        </div>
      );
    }
  };

  const renderStep2 = () => (
    <div className="booking-step step-2">
      <h2>Kunde auswählen</h2>

      <div className="booking-summary">
        <div className="summary-item">
          <Calendar size={18} />
          <span>{bookingData.date && format(bookingData.date, 'dd.MM.yyyy', { locale: de })}</span>
        </div>
        <div className="summary-item">
          <Clock size={18} />
          <span>{bookingData.slot?.time || bookingData.slot?.startTime || '-'}</span>
        </div>
        <div className="summary-item">
          <Users size={18} />
          <span>{bookingData.participants} Personen</span>
        </div>
      </div>

      <CustomerSelector
        onCustomerSelect={(customer) => {
          setSelectedCustomer(customer);
          setBookingData(prev => ({
            ...prev,
            firstName: customer.firstName,
            lastName: customer.lastName,
            email: customer.email,
            phone: customer.phone,
            company: customer.companyName || ''
          }));
        }}
        selectedCustomer={selectedCustomer}
      />

      {selectedCustomer && (
        <div className="selected-customer-preview" style={{
          marginTop: '1.5rem',
          padding: '1rem',
          background: '#f0f8ff',
          border: '1px solid #4a9eff',
          borderRadius: '8px'
        }}>
          <h3 style={{ marginTop: 0, color: '#1a2332' }}>Ausgewählter Kunde:</h3>
          <p style={{ margin: '0.5rem 0', color: '#333' }}>
            <strong>{selectedCustomer.firstName} {selectedCustomer.lastName}</strong>
          </p>
          <p style={{ margin: '0.5rem 0', color: '#666' }}>
            📧 {selectedCustomer.email}
          </p>
          {selectedCustomer.phone && (
            <p style={{ margin: '0.5rem 0', color: '#666' }}>
              📞 {selectedCustomer.phone}
            </p>
          )}
          {selectedCustomer.companyName && (
            <p style={{ margin: '0.5rem 0', color: '#666' }}>
              🏢 {selectedCustomer.companyName}
            </p>
          )}
        </div>
      )}

      <div className="form-group" style={{ marginTop: '1.5rem' }}>
        <label htmlFor="notes">Anmerkungen (optional)</label>
        <textarea
          id="notes"
          rows={4}
          value={bookingData.notes || ''}
          onChange={(e) => setBookingData(prev => ({ ...prev, notes: e.target.value }))}
          style={{
            width: '100%',
            padding: '0.75rem',
            border: '1px solid #ddd',
            borderRadius: '6px',
            fontSize: '1rem',
            fontFamily: 'inherit'
          }}
        />
      </div>

      <div className="form-actions" style={{ marginTop: '1.5rem' }}>
        <button type="button" onClick={() => setStep(1)} className="btn-secondary">
          Zurück
        </button>
        <button
          type="button"
          onClick={() => setStep(3)}
          className="btn-primary"
          disabled={!selectedCustomer}
        >
          Weiter zu Zusatzoptionen
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );

  const renderStep3AddOns = () => {
    // Use the actual slot tourType (e.g., "PREMIUM_ALL_IN_01") for addon API
    // NOT the mapped variant (e.g., "AllInPremium")
    const tourTypeForAddons = bookingData.slot?.tourType || bookingData.tourType;

    // Debug logging
    console.log('[Step3] Slot TourType:', bookingData.slot?.tourType);
    console.log('[Step3] TourType for Addons API:', tourTypeForAddons);
    console.log('[Step3] Participant Count:', bookingData.participants);

    return (
      <div className="booking-step step-3">
        <h2>Zusatzoptionen auswählen (Optional)</h2>

        <div className="booking-summary">
          <div className="summary-item">
            <Calendar size={18} />
            <span>{bookingData.date && format(bookingData.date, 'dd.MM.yyyy', { locale: de })}</span>
          </div>
          <div className="summary-item">
            <Clock size={18} />
            <span>{bookingData.slot?.time || bookingData.slot?.startTime || '-'}</span>
          </div>
          <div className="summary-item">
            <Users size={18} />
            <span>{bookingData.participants} Personen</span>
          </div>
        </div>

        <div style={{ marginTop: '1.5rem' }}>
          <AddonSelector
            tourType={tourTypeForAddons}
            // FIX 24.08.2026 (Kundin-Befund 25.08., Inklusivleistungen): Nicht zahlende
            // Kinder 0-2 haben KEINEN Anspruch auf Getränkeflat und Speise (Anlage v1.3
            // Abschnitt 2.1 Punkt 9). Inklusiv-Mengen daher ohne children0to2 vorbelegen,
            // zahlende Gäste und Freiplätze zählen weiterhin mit.
            participantCount={bookingData.participants - categories.children0to2}
            onAddonsChange={(addons) => {
              setCalculatedAddons(addons);
              // Save the parameters for which these addons were calculated
              // This allows us to detect when we need to recalculate
              if (addons.length > 0 && !lastAddonParams) {
                setLastAddonParams({
                  participants: bookingData.participants,
                  tourType: tourTypeForAddons
                });
              }
            }}
            initialAddons={calculatedAddons}
          />
        </div>

        {/* Validation Warning for Optional Choice Addons */}
        {(() => {
          const validation = validateOptionalChoiceAddons();
          if (validation.hasOptionalChoice && !validation.isValid) {
            return (
              <div style={{
                marginTop: '1.5rem',
                padding: '1rem',
                background: '#fef3c7',
                border: '2px solid #f59e0b',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <AlertCircle size={24} style={{ color: '#d97706', flexShrink: 0 }} />
                <div>
                  <strong style={{ color: '#92400e' }}>Verpflegungsauswahl unvollständig</strong>
                  <p style={{ margin: '0.25rem 0 0 0', color: '#a16207', fontSize: '0.9rem' }}>
                    {validation.multiplier > 1
                      ? `Die Summe der Portionen (${validation.total}) muss ${validation.expected} entsprechen (${mealRelevantCount} leistungsberechtigte Teilnehmer × ${validation.multiplier} pro Person, Kinder 0-2 ohne Verpflegung).`
                      : `Die Summe der Verpflegungsauswahl (${validation.total}) muss der Zahl der leistungsberechtigten Teilnehmer (${validation.expected}, ohne Kinder 0-2) entsprechen.`
                    }
                    {validation.total > validation.expected
                      ? ` Bitte reduzieren Sie die Auswahl um ${validation.total - validation.expected}.`
                      : ` Bitte wählen Sie noch ${validation.expected - validation.total} weitere Portion(en).`
                    }
                  </p>
                </div>
              </div>
            );
          }
          return null;
        })()}

        <div className="form-actions" style={{ marginTop: '1.5rem' }}>
          <button type="button" onClick={() => setStep(2)} className="btn-secondary">
            Zurück
          </button>
          <button
            type="button"
            onClick={() => setStep(4)}
            className="btn-primary"
            disabled={!canProceedToStep4()}
            style={!canProceedToStep4() ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            Weiter zur Prüfung
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    );
  };

  const renderStep4 = () => {
    console.log('[Step4] bookingData:', bookingData);
    console.log('[Step4] bookingData.tourType:', bookingData.tourType);
    console.log('[Step4] bookingData.slot?.tourType:', bookingData.slot?.tourType);

    // Calculate tiered price: Staffel NUR nach Vollzahlern (Erwachsene), Kategorie-Preise 100/60/20
    const tourTypeCode = bookingData.slot?.tourType || bookingData.tourType;
    const fallbackPrice = bookingData.slot?.pricePerPerson || bookingData.slot?.basePrice || 0;
    const tieredPriceResult = calculateTieredPrice(
      tourTypeCode,
      categories.adults,
      tourTypesWithPricing,
      fallbackPrice
    );
    const pricePerPerson = tieredPriceResult.pricePerPerson;
    // IMPORTANT: For FixedPrice tours, tourPrice IS the fixed price (not multiplied by participants)
    const isFixedPrice = tieredPriceResult.pricingMode === 'FixedPrice';
    const r2Step4 = (x: number) => Math.round(x * 100) / 100;
    const tourPrice = isFixedPrice
      ? pricePerPerson
      : categories.adults * pricePerPerson +
        categories.children6to12 * r2Step4(pricePerPerson * 0.6) +
        categories.children3to5 * r2Step4(pricePerPerson * 0.2);
    const addOnTotal = calculateAddOnTotal();
    const totalPrice = tourPrice + addOnTotal;

    console.log('[Step4] Tiered price calculation:', {
      tourTypeCode,
      participants: bookingData.participants,
      pricePerPerson,
      tierDescription: tieredPriceResult.tierDescription,
      pricingMode: tieredPriceResult.pricingMode,
      isFixedPrice,
      tourPrice
    });

    return (
      <div className={`booking-step step-4 ${isSubmitting ? 'is-submitting' : ''}`}>
        <h2>Buchung prüfen</h2>
        <div className="review-section">
          <h3>Tour-Details</h3>
          <div className="review-grid">
            <div className="review-item">
              <span className="label">Tour-Typ:</span>
              <span className="value">{bookingData.slot?.tourType || bookingData.tourType}</span>
            </div>
            <div className="review-item">
              <span className="label">Datum:</span>
              <span className="value">
                {bookingData.date && format(bookingData.date, 'EEEE, dd.MM.yyyy', { locale: de })}
              </span>
            </div>
            <div className="review-item">
              <span className="label">Uhrzeit:</span>
              <span className="value">{bookingData.slot?.time || bookingData.slot?.startTime || '-'}</span>
            </div>
            <div className="review-item">
              <span className="label">Teilnehmer:</span>
              <span className="value">
                {bookingData.participants} Personen
                {(categories.children6to12 > 0 || categories.children3to5 > 0 || categories.children0to2 > 0 || categories.freeSeats > 0) &&
                  ` (${categories.adults} Erwachsene, ${categories.children6to12} Kinder 6-12, ${categories.children3to5} Kinder 3-5, ${categories.children0to2} Kinder 0-2, ${categories.freeSeats} Freiplätze)`}
              </span>
            </div>
          </div>
        </div>

        <div className="review-section">
          <h3>Kontaktdaten</h3>
          <div className="review-grid">
            <div className="review-item">
              <span className="label">Name:</span>
              <span className="value">{bookingData.firstName} {bookingData.lastName}</span>
            </div>
            <div className="review-item">
              <span className="label">E-Mail:</span>
              <span className="value">{bookingData.email}</span>
            </div>
            <div className="review-item">
              <span className="label">Telefon:</span>
              <span className="value">{bookingData.phone}</span>
            </div>
            {bookingData.company && (
              <div className="review-item">
                <span className="label">Firma:</span>
                <span className="value">{bookingData.company}</span>
              </div>
            )}
          </div>
        </div>

        {calculatedAddons.filter(a => a.quantityTotal > 0).length > 0 && (
          <div className="review-section">
            <h3>Extras</h3>
            <div className="review-grid">
              {calculatedAddons
                .filter(addon => addon.quantityTotal > 0)
                .map(addon => {
                  // AUFGABE 1: Korrekte Anzeige - "inklusive" statt "kostenlos"
                  let statusText = '';
                  if (addon.quantityPaid === 0 && addon.quantityFree > 0) {
                    // Alles inklusive
                    statusText = `${addon.quantityTotal}x inklusive`;
                  } else if (addon.quantityFree > 0 && addon.quantityPaid > 0) {
                    // Teilweise inklusive
                    statusText = `${addon.quantityFree} inklusive, ${addon.quantityPaid} kostenpflichtig`;
                  } else {
                    // Alles kostenpflichtig
                    statusText = `${addon.quantityPaid}x kostenpflichtig`;
                  }

                  // Bruttopreis für dieses Addon berechnen
                  const grossPrice = addon.grossPricePerUnit ?? addon.pricePerUnit * (1 + (addon.taxRate ?? 0.19));
                  const grossSubtotal = addon.quantityPaid * grossPrice;

                  return (
                    <div key={addon.addOnServiceId} className="review-item">
                      <span className="label">{addon.addOnServiceName}:</span>
                      <span className="value">
                        {statusText}{grossSubtotal > 0 ? ` - ${formatCurrency(grossSubtotal)}` : ''}
                      </span>
                    </div>
                  );
                })}
            </div>

            {/* Extras Gesamt mit Steueraufschlüsselung */}
            {(() => {
              const taxBreakdown = calculateAddonTaxBreakdown();
              if (taxBreakdown.totalGross <= 0) return null;

              return (
                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  background: '#f0fdf4',
                  border: '2px solid #16a34a',
                  borderRadius: '8px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                  }}>
                    <div>
                      <strong style={{ color: '#1a2332' }}>Extras Gesamt</strong>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#16a34a' }}>
                        {formatCurrency(taxBreakdown.totalGross)}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#666' }}>
                        Netto: {formatCurrency(taxBreakdown.totalNet)}
                      </div>
                      {taxBreakdown.vat7 > 0 && (
                        <div style={{ fontSize: '0.85rem', color: '#666' }}>
                          MwSt. 7% (anteilig): {formatCurrency(taxBreakdown.vat7)}
                        </div>
                      )}
                      {taxBreakdown.vat19 > 0 && (
                        <div style={{ fontSize: '0.85rem', color: '#666' }}>
                          MwSt. 19% (anteilig): {formatCurrency(taxBreakdown.vat19)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Gutschein-Eingabe für Admin-Buchungen */}
        <div className="review-section">
          <h3>Gutschein einlösen (optional)</h3>
          <VoucherCodeInput
            onVoucherApplied={(voucher) => setAppliedVoucher(voucher)}
            onVoucherRemoved={() => setAppliedVoucher(null)}
            bookingTotal={totalPrice}
            disabled={isSubmitting}
          />
        </div>

        <div className="review-section price-section">
          <h3>Preis</h3>
          <div className="price-calculation">
            <div className="price-line">
              <span>
                {isFixedPrice
                  ? `Tour: Festpreis für ${bookingData.participants} Personen`
                  : `Tour: ${bookingData.participants} Personen × ${formatCurrency(pricePerPerson)} (${tieredPriceResult.tierDescription})`
                }
              </span>
              <span className="price-total">{formatCurrency(tourPrice)}</span>
            </div>
            {addOnTotal > 0 && (
              <div className="price-line">
                <span>Zusatzoptionen:</span>
                <span className="price-total">{formatCurrency(addOnTotal)}</span>
              </div>
            )}
            {appliedVoucher && (
              <div className="price-line" style={{ color: '#16a34a' }}>
                <span>Gutschein ({appliedVoucher.code}):</span>
                <span className="price-total">-{formatCurrency(appliedVoucher.amountToApply)}</span>
              </div>
            )}
            <div className="price-line total">
              <span>Gesamtpreis:</span>
              <span className="price-total">{formatCurrency(appliedVoucher ? Math.max(0, totalPrice - appliedVoucher.amountToApply) : totalPrice)}</span>
            </div>
          </div>
        </div>

        {bookingData.slot && (() => {
          // Berechnungen für die Verfügbarkeitsanzeige
          const maxCapacity = bookingData.slot.maxCapacity || bookingData.slot.maxParticipants || 0;
          const currentBookings = bookingData.slot.currentBookings || bookingData.slot.currentParticipants || 0;
          const minRequired = bookingData.slot.minCapacity || bookingData.slot.minParticipants || 0;
          const newBookingParticipants = bookingData.participants || 0;

          // Vorher (ohne diese Buchung)
          const availableBefore = maxCapacity - currentBookings;

          // Nachher (mit dieser Buchung)
          const availableAfter = availableBefore - newBookingParticipants;
          const totalAfter = currentBookings + newBookingParticipants;

          // Überbuchung?
          const isOverbooking = availableAfter < 0;
          const overbookingAmount = Math.abs(availableAfter);

          // Mindestgruppengröße erreicht?
          const isMinimumMetAfter = totalAfter >= minRequired;
          const remainingToMinimum = minRequired - totalAfter;

          return (
            <div className="availability-section">
              <h3>Verfügbarkeit</h3>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1rem',
                marginBottom: '1rem'
              }}>
                {/* VORHER */}
                <div style={{
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  padding: '1rem',
                  border: '1px solid #e0e0e0'
                }}>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    AKTUELL (vor dieser Buchung)
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
                    <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: availableBefore > 0 ? '#16a34a' : '#dc2626' }}>
                      {availableBefore}
                    </span>
                    <span style={{ color: '#666' }}>von {maxCapacity} Plätzen frei</span>
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                    {currentBookings} bereits gebucht
                  </div>
                </div>

                {/* NACHHER */}
                <div style={{
                  background: isOverbooking ? '#fef2f2' : '#f0fdf4',
                  borderRadius: '8px',
                  padding: '1rem',
                  border: isOverbooking ? '2px solid #dc2626' : '2px solid #16a34a'
                }}>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    NACH dieser Buchung (+{newBookingParticipants})
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
                    <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: isOverbooking ? '#dc2626' : '#16a34a' }}>
                      {isOverbooking ? 0 : availableAfter}
                    </span>
                    <span style={{ color: '#666' }}>von {maxCapacity} Plätzen frei</span>
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                    {totalAfter} gebucht
                  </div>
                </div>
              </div>

              {/* WARNUNGEN */}
              {isOverbooking && (
                <div style={{
                  background: '#fef2f2',
                  border: '2px solid #dc2626',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <span style={{ fontSize: '1.5rem' }}>⚠️</span>
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#dc2626' }}>
                      Überbuchung: {overbookingAmount} Plätze über Kapazität!
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#991b1b' }}>
                      Die Buchung übersteigt die maximale Kapazität von {maxCapacity} Personen.
                      Als Admin können Sie trotzdem fortfahren.
                    </div>
                  </div>
                </div>
              )}

              {!isMinimumMetAfter && remainingToMinimum > 0 && (
                <div style={{
                  background: '#fef9c3',
                  border: '1px solid #facc15',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <span style={{ fontSize: '1.5rem' }}>ℹ️</span>
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#854d0e' }}>
                      Mindestgruppengröße noch nicht erreicht
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#92400e' }}>
                      Noch {remainingToMinimum} Personen bis zur Mindestgruppengröße von {minRequired}.
                    </div>
                  </div>
                </div>
              )}

              {!isOverbooking && isMinimumMetAfter && (
                <div style={{
                  background: '#f0fdf4',
                  border: '1px solid #16a34a',
                  borderRadius: '8px',
                  padding: '0.75rem 1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{ color: '#16a34a' }}>✓</span>
                  <span style={{ color: '#15803d' }}>
                    Buchung möglich - {availableAfter} Plätze verbleiben nach dieser Buchung
                  </span>
                </div>
              )}
            </div>
          );
        })()}

        <div className="review-section">
          <h3>Buchungsstatus</h3>
          <div className="form-group">
            <label htmlFor="booking-status">Status:</label>
            <select
              id="booking-status"
              value={bookingStatus}
              onChange={(e) => setBookingStatus(e.target.value)}
              className="form-control"
            >
              <option value="Draft">Entwurf</option>
              <option value="OfferSent">Angebot</option>
              <option value="Option">Option (vorläufig)</option>
              <option value="Confirmed">Bestätigt</option>
              <option value="PaymentPending">Zahlung ausstehend</option>
              <option value="AwaitingFinalPayment">Warte auf Restzahlung</option>
              <option value="PartiallyPaid">Teilweise bezahlt</option>
              <option value="FullyPaid">Vollständig bezahlt</option>
              <option value="InProgress">In Bearbeitung</option>
              <option value="Completed">Abgeschlossen</option>
              <option value="Cancelled">Storniert</option>
            </select>
          </div>

          {/* Gültig-bis-Datepicker bei Status "Option" oder "Angebot" */}
          {(bookingStatus === 'Option' || bookingStatus === 'OfferSent') && (
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label htmlFor="option-valid-until">
                {bookingStatus === 'OfferSent' ? 'Angebot gültig bis' : 'Stichtag (Option gültig bis)'} <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="date"
                id="option-valid-until"
                value={optionValidUntil}
                onChange={(e) => setOptionValidUntil(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="form-control"
                required
                style={{ marginTop: '0.5rem' }}
              />
              <small style={{ color: '#666', marginTop: '0.25rem', display: 'block' }}>
                {bookingStatus === 'OfferSent'
                  ? 'Bis zu diesem Datum ist das Angebot gültig.'
                  : 'Bis zu diesem Datum muss die Option in eine Buchung umgewandelt werden.'}
              </small>
            </div>
          )}
        </div>

        {/* T-081 Altfall-Übernahme (v1.8.17): Buchungen aus dem Altverfahren mit bereits gezahlter Anzahlung */}
        <div className="review-section" data-testid="legacy-import-section">
          <h3>Übernahme Altfall</h3>
          <div className="form-group" style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
            <input
              type="checkbox"
              id="legacy-import"
              checked={isLegacyImport}
              onChange={(e) => setIsLegacyImport(e.target.checked)}
              style={{ marginTop: '0.3rem', width: '1.1rem', height: '1.1rem' }}
            />
            <label htmlFor="legacy-import" style={{ cursor: 'pointer', textTransform: 'none', letterSpacing: 'normal' }}>
              <strong>Übernahme Altfall (vor Systemstart gebucht)</strong>
              <small style={{ color: '#666', display: 'block', marginTop: '0.2rem' }}>
                Für Buchungen, die vor dem Start des Buchungssystems im bisherigen Verfahren gebucht wurden.
                Eine dort bereits in Rechnung gestellte und bezahlte Anzahlung wird hier nur vermerkt:
                Es entsteht keine neue Anzahlungsrechnung und keine E-Mail, die Schlussrechnung zieht den Betrag automatisch ab.
              </small>
            </label>
          </div>

          {isLegacyImport && (
            <div style={{ background: '#fef9ec', border: '1px solid #f5d78e', borderRadius: '8px', padding: '0.75rem 1rem', marginTop: '0.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? '1fr 1fr' : '1fr', gap: '0.75rem' }}>
                <div className="form-group">
                  <label htmlFor="legacy-deposit-amount">Anzahlung bereits gezahlt (Betrag in €)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    id="legacy-deposit-amount"
                    className="form-control"
                    placeholder="z. B. 330,00 (leer = noch keine Anzahlung gezahlt)"
                    value={legacyDepositAmount}
                    onChange={(e) => setLegacyDepositAmount(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="legacy-deposit-paid-at">Gezahlt am</label>
                  <input
                    type="date"
                    id="legacy-deposit-paid-at"
                    className="form-control"
                    value={legacyDepositPaidAt}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={(e) => setLegacyDepositPaidAt(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="legacy-invoice-number">Alte Rechnungsnummer (Altverfahren)</label>
                  <input
                    type="text"
                    id="legacy-invoice-number"
                    className="form-control"
                    maxLength={50}
                    placeholder="z. B. 2026-0815"
                    value={legacyInvoiceNumber}
                    onChange={(e) => setLegacyInvoiceNumber(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="legacy-note">Notiz zur Übernahme (intern)</label>
                  <input
                    type="text"
                    id="legacy-note"
                    className="form-control"
                    maxLength={500}
                    placeholder="z. B. Katalogreise, Anzahlung per Überweisung"
                    value={legacyNote}
                    onChange={(e) => setLegacyNote(e.target.value)}
                  />
                </div>
              </div>
              <small style={{ color: '#92400e', display: 'block', marginTop: '0.5rem' }}>
                Mit Betrag: Die Schlussrechnung weist die Anzahlung als "Anzahlung laut Rechnung {legacyInvoiceNumber || '...'} (Altverfahren)" aus und zieht sie ab;
                eine neue Anzahlungsrechnung ist dann gesperrt. Ohne Betrag: Die Anzahlung ist noch offen und kann wie gewohnt in Rechnung gestellt werden.
                Der Gast erhält bei der Übernahme keine E-Mail; den Zugangslink senden Sie bei Bedarf über die Kundenverwaltung.
              </small>
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="button" onClick={() => setStep(3)} className="btn-secondary" disabled={isSubmitting}>
            Zurück
          </button>
          <button type="button" onClick={handleSubmit} className="btn-primary" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 size={18} className="spinner" />
                Wird gespeichert...
              </>
            ) : (
              <>
                {bookingStatus === 'OfferSent'
                  ? 'Angebot speichern'
                  : bookingStatus === 'Option'
                  ? 'Option speichern'
                  : bookingStatus === 'Draft'
                  ? 'Entwurf speichern'
                  : 'Buchung speichern'}
                <ChevronRight size={18} />
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  const renderStep5 = () => {
    // Check if this is a quote (Angebot)
    const isQuote = bookingStatus === 'OfferSent';

    // Generate quote number from booking number
    // Handles formats: "FB-2026-001" -> "ANG-2026-001", "B260082" -> "ANG-2026-082"
    const formatQuoteNumber = (bookingNumber: string): string => {
      if (!bookingNumber) return '';

      // If already starts with ANG, return as-is
      if (bookingNumber.startsWith('ANG-')) return bookingNumber;

      // Format: FB-2026-001 -> ANG-2026-001
      if (bookingNumber.startsWith('FB-')) {
        return bookingNumber.replace('FB-', 'ANG-');
      }

      // Format: B260082 -> ANG-2026-082 (extract year and number)
      const match = bookingNumber.match(/^B(\d{2})(\d{4})$/);
      if (match) {
        const year = '20' + match[1]; // "26" -> "2026"
        const num = match[2]; // "0082"
        return `ANG-${year}-${num}`;
      }

      // Fallback: just prefix with ANG-
      return `ANG-${bookingNumber}`;
    };

    const quoteNumber = createdBooking?.quoteNumber || formatQuoteNumber(createdBooking?.bookingNumber || '');

    return (
    <div className="booking-step step-5">
      <div className="confirmation-container">
        <div className="confirmation-icon">
          <CheckCircle size={64} color="#16a34a" />
        </div>
        <h2>{isQuote ? 'Angebot erfolgreich!' : 'Buchung erfolgreich!'}</h2>

        {/* Display booking/quote number prominently */}
        {createdBooking?.bookingNumber && (
          <div className="booking-number-display" style={{
            background: '#f0fdf4',
            border: '2px solid #16a34a',
            borderRadius: '8px',
            padding: '1rem 1.5rem',
            margin: '1rem 0',
            textAlign: 'center'
          }}>
            <span style={{ color: '#666', fontSize: '0.9rem' }}>
              {isQuote ? 'Ihre Angebotsnummer:' : 'Ihre Buchungsnummer:'}
            </span>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#16a34a', marginTop: '0.25rem' }}>
              {isQuote ? quoteNumber : createdBooking.bookingNumber}
            </div>
            {/* Show booking number less prominently for quotes */}
            {isQuote && createdBooking.bookingNumber && (
              <div style={{ fontSize: '0.85rem', color: '#888', marginTop: '0.5rem' }}>
                Buchungsnummer: {createdBooking.bookingNumber}
              </div>
            )}
          </div>
        )}

        <p>Vielen Dank für Ihre {isQuote ? 'Anfrage' : (bookingData.participants >= 30 ? 'Buchung' : 'Anfrage')}!</p>

        <div className="confirmation-details">
          <p>
            {isQuote
              ? 'Das Angebot wurde erfolgreich gespeichert.'
              : `Die ${bookingData.participants >= 30 ? 'Buchung' : 'Anfrage'} wurde erfolgreich gespeichert.`}
          </p>
          {/* T-042: klarer Versandstand statt "wird separat versendet" */}
          {isQuote ? (
            quoteEmailSentTo ? (
              <p style={{ color: '#15803d', fontSize: '0.95rem', fontWeight: 600 }}>
                Angebots-Mail mit PDF versendet an {quoteEmailSentTo}.
              </p>
            ) : (
              <p style={{ color: '#b45309', fontSize: '0.95rem', fontWeight: 600 }}>
                Das Angebot ist gespeichert, aber noch NICHT an den Kunden versendet.
                Mit "Per E-Mail senden" geht das Angebot mit PDF
                {selectedCustomer?.email ? ` an ${selectedCustomer.email}` : ' an die Kunden-E-Mail'} raus.
              </p>
            )
          ) : (
            <p style={{ color: '#666', fontSize: '0.9rem' }}>
              Hinweis: Die Bestätigung und Rechnung werden separat vom Admin erstellt und versendet.
            </p>
          )}
        </div>

        <div className="confirmation-summary">
          <h3>{isQuote ? 'Ihre Angebotsdetails:' : 'Ihre Buchungsdetails:'}</h3>
          <ul>
            {/* Show booking or quote number in summary list */}
            {createdBooking?.bookingNumber && (
              <li><strong>{isQuote ? 'Angebotsnummer' : 'Buchungsnummer'}: {isQuote ? quoteNumber : createdBooking.bookingNumber}</strong></li>
            )}
            <li>Datum: {bookingData.date && format(bookingData.date, 'dd.MM.yyyy')}</li>
            <li>Zeit: {bookingData.slot?.time || bookingData.slot?.startTime || '-'}</li>
            <li>Teilnehmer: {bookingData.participants} Personen</li>
            <li>Tour-Typ: {bookingData.slot?.tourType || bookingData.tourType}</li>
            {calculatedAddons.filter(a => a.quantityTotal > 0).length > 0 && (
              <li>Zusatzoptionen: {calculatedAddons.filter(a => a.quantityTotal > 0).length} ausgewählt</li>
            )}
            {/* T-081 Altfall-Übernahme */}
            {isLegacyImport && (
              <li>
                <strong>Übernahme Altfall:</strong>{' '}
                {legacyDepositAmount.trim() && parseFloat(legacyDepositAmount.replace(',', '.')) > 0
                  ? `Anzahlung ${formatCurrency(parseFloat(legacyDepositAmount.replace(',', '.')))} bereits gezahlt${legacyDepositPaidAt ? ` am ${new Date(legacyDepositPaidAt).toLocaleDateString('de-DE')}` : ''}${legacyInvoiceNumber.trim() ? `, Rechnung ${legacyInvoiceNumber.trim()} (Altverfahren)` : ''}, keine neue Anzahlungsrechnung, Abzug in der Schlussrechnung`
                  : 'ohne Alt-Anzahlung (Anzahlung noch offen)'}
                , keine E-Mail an den Gast
              </li>
            )}
          </ul>

          {/* FIX: Show price summary with tiered pricing AND voucher discount */}
          {(() => {
            // Calculate tiered price based on participant count
            const tourTypeCodeStep5 = bookingData.slot?.tourType || bookingData.tourType;
            const fallbackPriceStep5 = bookingData.slot?.pricePerPerson || bookingData.slot?.basePrice || 0;
            const tieredPriceStep5 = calculateTieredPrice(
              tourTypeCodeStep5,
              categories.adults,
              tourTypesWithPricing,
              fallbackPriceStep5
            );
            const pricePerPersonStep5 = tieredPriceStep5.pricePerPerson;
            // IMPORTANT: For FixedPrice tours, tourPrice IS the fixed price (not multiplied by participants)
            const isFixedPriceStep5 = tieredPriceStep5.pricingMode === 'FixedPrice';
            const r2Step5 = (x: number) => Math.round(x * 100) / 100;
            const tourPrice = isFixedPriceStep5
              ? pricePerPersonStep5
              : categories.adults * pricePerPersonStep5 +
                categories.children6to12 * r2Step5(pricePerPersonStep5 * 0.6) +
                categories.children3to5 * r2Step5(pricePerPersonStep5 * 0.2);

            // FIX: Use BRUTTO prices for add-on total (addon.subtotal is NETTO)
            const addOnTotal = calculatedAddons
              .filter(a => a.quantityTotal > 0)
              .reduce((sum, addon) => {
                const grossPrice = addon.grossPricePerUnit ?? addon.pricePerUnit * (1 + (addon.taxRate ?? 0.19));
                return sum + addon.quantityPaid * grossPrice;
              }, 0);
            const totalGross = tourPrice + addOnTotal;

            // FIX: Apply voucher discount
            const voucherDiscount = appliedVoucher?.amountToApply || 0;
            const finalTotal = Math.max(0, totalGross - voucherDiscount);

            return (
              <div className="price-summary" style={{
                marginTop: '1.5rem',
                padding: '1rem',
                background: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <h4 style={{ margin: '0 0 0.75rem 0', color: '#334155' }}>Preisübersicht:</h4>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                  <span>
                    {isFixedPriceStep5
                      ? `Tour (Festpreis für ${bookingData.participants} Pers.):`
                      : (categories.children6to12 > 0 || categories.children3to5 > 0)
                        ? `Tour (${categories.adults} Erw. × ${formatCurrency(pricePerPersonStep5)}${categories.children6to12 > 0 ? `, ${categories.children6to12} Ki. 6-12 × ${formatCurrency(r2Step5(pricePerPersonStep5 * 0.6))}` : ''}${categories.children3to5 > 0 ? `, ${categories.children3to5} Ki. 3-5 × ${formatCurrency(r2Step5(pricePerPersonStep5 * 0.2))}` : ''}):`
                        : `Tour (${categories.adults} Pers. × ${formatCurrency(pricePerPersonStep5)}):`
                    }
                  </span>
                  <span>{formatCurrency(tourPrice)}</span>
                </div>
                {addOnTotal > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                    <span>Zusatzoptionen:</span>
                    <span>{formatCurrency(addOnTotal)}</span>
                  </div>
                )}
                {appliedVoucher && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem', color: '#16a34a' }}>
                    <span>Gutschein ({appliedVoucher.code}):</span>
                    <span>-{formatCurrency(voucherDiscount)}</span>
                  </div>
                )}
                <div style={{ borderTop: '1px solid #cbd5e1', marginTop: '0.5rem', paddingTop: '0.5rem' }}>
                  {/* MwSt-Aufschlüsselung ausgeblendet - gemischte MwSt-Sätze (7%/19%) */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '1.1rem', color: '#16a34a' }}>
                    <span>Gesamtbetrag (brutto):</span>
                    <span>{formatCurrency(finalTotal)}</span>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Action buttons for quotes: PDF download and email send */}
        {isQuote && createdBooking?.id && (
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginBottom: '1rem',
            padding: '1rem',
            background: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <button
              type="button"
              onClick={async () => {
                // Download PDF with Authorization header
                const pdfUrl = `${API_BASE_URL}/document/offer/${createdBooking.id}`;
                console.log('PDF Download URL:', pdfUrl);
                try {
                  const response = await fetch(pdfUrl, {
                    method: 'GET',
                    headers: {
                      'Authorization': `Bearer ${token}`
                    }
                  });
                  if (response.ok) {
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `angebot-ANG-${createdBooking.bookingNumber?.replace('B', '')}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                  } else {
                    const error = await response.text();
                    alert('Fehler beim PDF-Download: ' + (error || response.statusText));
                  }
                } catch (error: any) {
                  alert('Fehler beim PDF-Download: ' + error.message);
                }
              }}
              className="btn-secondary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              📄 PDF herunterladen
            </button>

            <button
              type="button"
              disabled={isSendingOfferEmail}
              onClick={async () => {
                // T-052: Doppelklick-Sperre, sonst geht die Angebots-Mail zweimal raus
                if (isSendingOfferEmail) return;
                setIsSendingOfferEmail(true);
                const emailUrl = `${API_BASE_URL}/document/offer/${createdBooking.id}/send`;
                console.log('Email Send URL:', emailUrl);
                console.log('Token:', token ? 'exists' : 'missing');
                try {
                  const response = await fetch(emailUrl, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    }
                  });
                  const result = await response.json();
                  if (response.ok) {
                    // T-042: Versandstand auf der Erfolgsseite merken
                    setQuoteEmailSentTo(selectedCustomer?.email || 'Kunden-E-Mail');
                    alert(result.message || 'Angebot erfolgreich per E-Mail versendet!');
                  } else {
                    alert('Fehler: ' + (result.error || result.message || 'E-Mail konnte nicht gesendet werden'));
                  }
                } catch (error: any) {
                  alert('Fehler beim E-Mail-Versand: ' + error.message);
                } finally {
                  setIsSendingOfferEmail(false);
                }
              }}
              className="btn-secondary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {isSendingOfferEmail ? '✉️ Wird gesendet ...' : '✉️ Per E-Mail senden'}
            </button>

            <button
              type="button"
              onClick={() => navigate(`/admin/bookings?search=${encodeURIComponent(createdBooking.bookingNumber || '')}`)}
              className="btn-secondary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              🔍 In Buchungen anzeigen
            </button>
          </div>
        )}

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            {isQuote ? 'Neues Angebot' : 'Neue Buchung'}
          </button>

          {/* Show "Angebote >" button only for quotes */}
          {isQuote && (
            <button
              type="button"
              onClick={() => navigate('/admin/quotes')}
              className="btn-secondary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              Angebote
              <ChevronRight size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
  };

  return (
    <div className="booking-flow">
      {renderProgressBar()}

      <div className="booking-content">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3AddOns()}
        {step === 4 && renderStep4()}
        {step === 5 && renderStep5()}
      </div>

      {/* AddOn Create Modal */}
      {showAddOnModal && (
        <div className="modal-overlay" onClick={() => setShowAddOnModal(false)}>
          <div className="modal-content customer-form" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Neue Zusatzoption erstellen</h3>
              <button onClick={() => setShowAddOnModal(false)} className="btn-close">×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => { e.preventDefault(); handleSaveNewAddOn(); }}>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Kategorie *</label>
                    <select
                      value={addOnFormData.categoryId}
                      onChange={(e) => setAddOnFormData({...addOnFormData, categoryId: parseInt(e.target.value)})}
                      required
                    >
                      {addOnCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group full-width">
                    <label>Name *</label>
                    <input
                      type="text"
                      value={addOnFormData.name}
                      onChange={(e) => setAddOnFormData({...addOnFormData, name: e.target.value})}
                      required
                      placeholder="z.B. Premium Bierverkostung"
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Beschreibung *</label>
                    <textarea
                      value={addOnFormData.description}
                      onChange={(e) => setAddOnFormData({...addOnFormData, description: e.target.value})}
                      required
                      rows={3}
                      placeholder="Beschreiben Sie diese Zusatzoption..."
                    />
                  </div>

                  <div className="form-group">
                    <label>Bruttopreis (€) *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={addOnFormData.grossPrice}
                      onChange={(e) => setAddOnFormData({...addOnFormData, grossPrice: parseFloat(e.target.value) || 0})}
                      required
                      min="0.01"
                    />
                  </div>

                  <div className="form-group">
                    <label>Preistyp *</label>
                    <select
                      value={addOnFormData.pricingType}
                      onChange={(e) => setAddOnFormData({...addOnFormData, pricingType: e.target.value as any})}
                      required
                    >
                      <option value="PerPerson">Pro Person</option>
                      <option value="PerGroup">Pro Gruppe</option>
                      <option value="Fixed">Festpreis</option>
                      <option value="PerDay">Pro Tag</option>
                      <option value="PerNight">Pro Nacht</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Steuersatz *</label>
                    <select
                      value={addOnFormData.taxRate}
                      onChange={(e) => setAddOnFormData({...addOnFormData, taxRate: parseFloat(e.target.value)})}
                    >
                      <option value="0.19">19% (Standard)</option>
                      <option value="0.07">7% (Ermäßigt)</option>
                      <option value="0">0% (Steuerfrei)</option>
                    </select>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary">
                    Erstellen und zur Buchung hinzufügen
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddOnModal(false)}
                    className="btn-secondary"
                  >
                    Abbrechen
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingFlow;
